'use client';

import { c as _c } from "react/compiler-runtime";
import { useCurrentLanguage } from '@repo/i18n/hooks/use-current-language';
import { useGetGameMapMetadataQuery, useGetGameMapQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsInCreatorMode, selectMapList, selectTitleSlug, setCompleteMapData } from '@repo/store/maps/slices/mapData';
import { createPublicMapUrl } from '@repo/types/maps/map-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export function useMapSwitcher() {
  const $ = _c(51);
  const titleSlug = useAppSelector(selectTitleSlug);
  const maps = useAppSelector(selectMapList);
  const currentMapExternalId = useAppSelector(selectExternalMapId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const currentLanguage = useCurrentLanguage();
  const dispatch = useAppDispatch();
  const [pendingTargetId, setPendingTargetId] = useState(null);
  const pendingLocationIdRef = useRef(null);
  const shouldUpdatePathRef = useRef(false);
  const lastRequestedIdRef = useRef(null);
  const mapsRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== maps) {
    t0 = () => {
      mapsRef.current = maps;
    };
    t1 = [maps];
    $[0] = maps;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== maps || $[4] !== pendingTargetId) {
    t2 = maps?.find(map => map.externalId === pendingTargetId);
    $[3] = maps;
    $[4] = pendingTargetId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectedMapInfo = t2;
  const shouldSkipMap = !pendingTargetId || pendingTargetId === currentMapExternalId;
  const t3 = pendingTargetId as string;
  let t4;
  if ($[6] !== currentLanguage || $[7] !== isInCreatorMode || $[8] !== t3) {
    t4 = {
      externalId: t3,
      locale: currentLanguage,
      debug: isInCreatorMode
    };
    $[6] = currentLanguage;
    $[7] = isInCreatorMode;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== shouldSkipMap) {
    t5 = {
      skip: shouldSkipMap
    };
    $[10] = shouldSkipMap;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: newMapData,
    isLoading: isLoadingNewMap,
    isError,
    error
  } = useGetGameMapQuery(t4, t5);
  const shouldSkipMetadata = !pendingTargetId || pendingTargetId === currentMapExternalId || !titleSlug || !selectedMapInfo?.slug;
  const t6 = titleSlug as string;
  const t7 = selectedMapInfo?.slug as string;
  let t8;
  if ($[12] !== currentLanguage || $[13] !== isInCreatorMode || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      titleSlug: t6,
      mapSlug: t7,
      locale: currentLanguage,
      debug: isInCreatorMode
    };
    $[12] = currentLanguage;
    $[13] = isInCreatorMode;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== shouldSkipMetadata) {
    t9 = {
      skip: shouldSkipMetadata
    };
    $[17] = shouldSkipMetadata;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    data: newMapConfig,
    isLoading: isLoadingNewMapConfig
  } = useGetGameMapMetadataQuery(t8, t9);
  const isSwitching = (isLoadingNewMap || isLoadingNewMapConfig || !!pendingTargetId) && pendingTargetId !== currentMapExternalId;
  let t10;
  if ($[19] !== titleSlug) {
    t10 = (mapExternalId, mapsList) => {
      if (!titleSlug) {
        return;
      }
      const mapInfo = mapsList?.find(map_0 => map_0.externalId === mapExternalId);
      if (mapInfo?.slug) {
        const baseUrl = typeof window !== "undefined" ? window.location.origin : undefined;
        const mapUrl = createPublicMapUrl({
          titleSlug,
          mapSlug: mapInfo.slug
        }, baseUrl);
        if (shouldUpdatePathRef.current) {
          try {
            const url = new URL(mapUrl);
            window.history.replaceState({}, "", `${url.pathname}${url.search}`);
          } catch {}
        }
      }
    };
    $[19] = titleSlug;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const navigateToMap = t10;
  let t11;
  let t12;
  if ($[21] !== currentMapExternalId || $[22] !== dispatch || $[23] !== isLoadingNewMap || $[24] !== isLoadingNewMapConfig || $[25] !== navigateToMap || $[26] !== newMapConfig || $[27] !== newMapData || $[28] !== pendingTargetId) {
    t11 = () => {
      if (!pendingTargetId || pendingTargetId === currentMapExternalId) {
        return;
      }
      const isLoading = isLoadingNewMap || isLoadingNewMapConfig;
      if (newMapData && mapsRef.current && newMapConfig && !isLoading && lastRequestedIdRef.current === pendingTargetId) {
        dispatch(setCompleteMapData({
          mapData: newMapData,
          mapInfo: mapsRef.current,
          mapConfig: newMapConfig
        }));
        navigateToMap(pendingTargetId, mapsRef.current);
        const pendingLocationId = pendingLocationIdRef.current;
        if (pendingLocationId) {
          try {
            const url_0 = new URL(window.location.href);
            url_0.searchParams.set("location", pendingLocationId);
            window.history.replaceState({}, "", url_0.toString());
          } catch {}
          pendingLocationIdRef.current = null;
        }
      }
    };
    t12 = [pendingTargetId, currentMapExternalId, newMapData, dispatch, newMapConfig, navigateToMap, isLoadingNewMap, isLoadingNewMapConfig];
    $[21] = currentMapExternalId;
    $[22] = dispatch;
    $[23] = isLoadingNewMap;
    $[24] = isLoadingNewMapConfig;
    $[25] = navigateToMap;
    $[26] = newMapConfig;
    $[27] = newMapData;
    $[28] = pendingTargetId;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[31] !== currentMapExternalId || $[32] !== pendingTargetId) {
    t13 = () => {
      if (pendingTargetId && currentMapExternalId === pendingTargetId && lastRequestedIdRef.current === pendingTargetId) {
        shouldUpdatePathRef.current = false;
        setPendingTargetId(null);
        lastRequestedIdRef.current = null;
      }
    };
    t14 = [currentMapExternalId, pendingTargetId];
    $[31] = currentMapExternalId;
    $[32] = pendingTargetId;
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[35] !== error || $[36] !== isError) {
    t15 = () => {
      if (isError && error) {
        setPendingTargetId(null);
        pendingLocationIdRef.current = null;
        shouldUpdatePathRef.current = false;
      }
    };
    t16 = [isError, error];
    $[35] = error;
    $[36] = isError;
    $[37] = t15;
    $[38] = t16;
  } else {
    t15 = $[37];
    t16 = $[38];
  }
  useEffect(t15, t16);
  let t17;
  if ($[39] !== currentMapExternalId) {
    t17 = (targetExternalId, options) => {
      if (!targetExternalId || targetExternalId === currentMapExternalId) {
        return;
      }
      lastRequestedIdRef.current = targetExternalId;
      pendingLocationIdRef.current = options?.focusLocationId ?? null;
      shouldUpdatePathRef.current = options?.updatePath ?? false;
      setPendingTargetId(targetExternalId);
    };
    $[39] = currentMapExternalId;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const switchMap = t17;
  const t18 = (maps?.length ?? 0) > 1;
  let t19;
  if ($[41] !== currentMapExternalId || $[42] !== error || $[43] !== isError || $[44] !== isLoadingNewMap || $[45] !== isSwitching || $[46] !== maps || $[47] !== pendingTargetId || $[48] !== switchMap || $[49] !== t18) {
    t19 = {
      isLoadingNewMap,
      isSwitching,
      isError,
      error,
      availableMaps: maps,
      currentMapExternalId,
      hasMultipleMaps: t18,
      switchMap,
      pendingExternalId: pendingTargetId
    };
    $[41] = currentMapExternalId;
    $[42] = error;
    $[43] = isError;
    $[44] = isLoadingNewMap;
    $[45] = isSwitching;
    $[46] = maps;
    $[47] = pendingTargetId;
    $[48] = switchMap;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
}